import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import DashboardLayout from "./layouts/DashboardLayout";

// Public Pages
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetails from "./pages/ProductDetails";
import Applications from "./pages/public/Applications";
import WhatWeDo from "./pages/public/WhatWeDo";
import About from "./pages/public/About";
import Gallery from "./pages/public/Gallery";
import Contact from "./pages/public/Contact";
import LumigoChatbot from "./components/public/LumigoChatbot";

// Legal Pages
import Terms from "./pages/public/Terms";
import Privacy from "./pages/public/Privacy";
import ShippingPolicy from "./pages/public/ShippingPolicy";
import RefundPolicy from "./pages/public/RefundPolicy";
import WarrantyPolicy from "./pages/public/WarrantyPolicy";

// Auth Pages
import Login from "./pages/auth/Login";
import AdminLogin from "./pages/auth/AdminLogin";
import Register from "./pages/auth/Register";

// Two Application Dashboards
import AdminDashboard from "./pages/dashboards/AdminDashboard";
import UserDashboard from "./pages/dashboards/UserDashboard";

const Unauthorized = () => {
    const { logout } = useAuth();

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-white/10 p-8 rounded-2xl text-center max-w-md w-full shadow-2xl">
                <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest block mb-2">
                    Access Denied
                </span>
                <h1 className="text-xl font-bold">Unauthorized Portal Access</h1>
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                    You do not possess the required administrator privileges to view this section.
                </p>
                <div className="mt-6 flex justify-center gap-4">
                    <button
                        onClick={() => window.location.href = '/'}
                        className="text-xs text-gray-400 hover:text-white underline"
                    >
                        Return Home
                    </button>
                    <button
                        onClick={() => { logout(); window.location.href = '/login'; }}
                        className="bg-[#D4AF37] hover:bg-[#b5952f] text-black px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition"
                    >
                        Switch Account
                    </button>
                </div>
            </div>
        </div>
    );
};

const App = () => {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    {/* Public B2B Website Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/product/:id" element={<ProductDetails />} />
                    <Route path="/applications" element={<Applications />} />
                    <Route path="/what-we-do" element={<WhatWeDo />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/contact" element={<Contact />} />

                    {/* Legal Pages */}
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/shipping-policy" element={<ShippingPolicy />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route path="/warranty" element={<WarrantyPolicy />} />

                    {/* Authentication Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/auth/admin-login" element={<AdminLogin />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Redirects for legacy links */}
                    <Route path="/inquiry" element={<Navigate to="/contact" replace />} />
                    <Route path="/partner" element={<Navigate to="/contact" replace />} />
                    <Route path="/cart" element={<Navigate to="/products" replace />} />
                    <Route path="/checkout" element={<Navigate to="/products" replace />} />

                    {/* EXACTLY TWO APPLICATION DASHBOARDS */}
                    <Route element={<ProtectedRoute />}>
                        <Route element={<DashboardLayout />}>

                            {/* 1. Admin Dashboard (Admin Only) */}
                            <Route element={<ProtectedRoute allowedRoles={["admin", "superadmin"]} />}>
                                <Route path="/admin" element={<AdminDashboard />} />
                                <Route path="/admin/inquiries" element={<Navigate to="/admin" replace />} />
                                <Route path="/admin/clients" element={<Navigate to="/admin" replace />} />
                            </Route>

                            {/* 2. User Dashboard (Customer Portal) */}
                            <Route element={<ProtectedRoute allowedRoles={["customer", "admin", "superadmin"]} />}>
                                <Route path="/customer" element={<UserDashboard />} />
                                <Route path="/customer/orders" element={<Navigate to="/customer" replace />} />
                                <Route path="/customer/wishlist" element={<Navigate to="/customer" replace />} />
                            </Route>

                            {/* Legacy role redirects */}
                            <Route path="/brand/*" element={<Navigate to="/admin" replace />} />
                            <Route path="/dealer/*" element={<Navigate to="/admin" replace />} />

                        </Route>
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                {/* Floating AI Assistant & WhatsApp Quick Help Desk (+91 8999427831) */}
                <LumigoChatbot />

                <Toaster
                    position="top-right"
                    containerStyle={{ top: 80 }}
                    toastOptions={{
                        duration: 3000,
                        style: {
                            background: '#181818',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.1)',
                            fontSize: '12px',
                        },
                    }}
                />
            </BrowserRouter>
        </AuthProvider>
    );
};

export default App;
